import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Images, X, Link2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { GET_MEDIA_ASSETS } from '../../graphql';
import { Loader } from '../common/PageLoader';

/**
 * The item's display image: pick one from the media library, or paste a URL.
 *
 * This sets a DISPLAY pointer only. The server routes it to
 * topics.primary_media_url rather than media_url, because media_url is part of
 * the natural identity key and is pinned to '' for cards and WordPress posts —
 * writing a URL into it would change the item's identity and make the next
 * ingest push insert a duplicate.
 *
 * Note the precedence: images attached on the Images tab are resolved from
 * media_links and overwrite this pointer whenever they change. This field is
 * the fallback for items with no attached media (an RSS thumbnail, say), not a
 * competing source of truth.
 */

interface LibraryAsset {
  id: string;
  originalFilename: string | null;
  sha256: string;
  renditions: { kind: string; url: string | null; isCurrent: boolean }[];
}

function currentUrl(a: LibraryAsset): string | null {
  const cur = a.renditions?.filter((r) => r.isCurrent) ?? [];
  return (
    cur.find((r) => r.kind === 'canonical')?.url ??
    cur.find((r) => r.kind === 'web')?.url ??
    cur.find((r) => r.kind === 'original')?.url ??
    cur.find((r) => r.kind === 'thumb')?.url ??
    null
  );
}

export function MediaUrlField({
  accountId,
  value,
  onChange,
  disabled,
  inputClassName,
  hasAttachedImages,
}: {
  accountId?: string | null;
  value: string;
  onChange: (url: string) => void;
  disabled?: boolean;
  inputClassName?: string;
  /** When true, attached images win and this pointer is only a fallback. */
  hasAttachedImages?: boolean;
}) {
  const [picking, setPicking] = useState(false);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          disabled={disabled}
          className={inputClassName}
        />
        {accountId && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1 shrink-0"
            disabled={disabled}
            onClick={() => setPicking(true)}
          >
            <Images className="w-3 h-3" /> Library
          </Button>
        )}
        {value && !disabled && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="shrink-0"
            title="Clear"
            onClick={() => onChange('')}
          >
            <X className="w-3 h-3" />
          </Button>
        )}
      </div>

      {value && (
        <div className="flex items-start gap-3">
          <div className="w-20 h-24 bg-cmp-surface rounded overflow-hidden flex items-center justify-center shrink-0">
            {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
            <img
              src={value}
              alt="Selected media"
              className="w-full h-full object-contain"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
          <p className="text-[11px] text-cmp-ink-2 break-all flex items-center gap-1 pt-1">
            <Link2 className="w-3 h-3 shrink-0" />
            {value}
          </p>
        </div>
      )}

      {hasAttachedImages && (
        <p className="text-[11px] text-cmp-ink-2">
          This item has attached images — those take precedence and will replace this pointer when
          they change. Manage them on the Images tab.
        </p>
      )}

      {picking && accountId && (
        <MediaUrlPicker
          accountId={accountId}
          onPick={(url) => {
            onChange(url);
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  );
}

/** Modal grid of the account's media that returns a URL rather than an asset. */
function MediaUrlPicker({
  accountId,
  onPick,
  onClose,
}: {
  accountId: string;
  onPick: (url: string) => void;
  onClose: () => void;
}) {
  const [filter, setFilter] = useState('');
  const { data, loading } = useQuery(GET_MEDIA_ASSETS, {
    variables: { accountId, limit: 60, offset: 0 },
    fetchPolicy: 'cache-and-network',
  });

  const assets: LibraryAsset[] = data?.mediaAssets?.assets ?? [];
  const shown = filter
    ? assets.filter((a) => (a.originalFilename || '').toLowerCase().includes(filter.toLowerCase()))
    : assets;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6">
      <Card className="w-full max-w-4xl max-h-[80vh] overflow-auto p-5 space-y-4 bg-cmp-raise border border-cmp-line text-cmp-ink shadow-2xl">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-cmp-ink">Choose an image</h3>
            <p className="text-xs text-cmp-ink-2 mt-0.5">
              Sets this item&rsquo;s display image. To attach images with a role (front, back), use
              the Images tab.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by filename…"
              className="w-56"
            />
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {loading && <Loader label="Loading library" className="text-sm text-cmp-ink-2" />}
        {!loading && shown.length === 0 && (
          <div className="text-sm text-cmp-ink-2">Nothing in the library matches.</div>
        )}

        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {shown.map((a) => {
            const url = currentUrl(a);
            if (!url) return null;
            return (
              <button
                key={a.id}
                onClick={() => onPick(url)}
                className="text-left group"
                title={a.originalFilename || a.sha256.slice(0, 12)}
              >
                <div className="aspect-[3/4] bg-cmp-surface rounded overflow-hidden flex items-center justify-center group-hover:ring-1 group-hover:ring-cmp-bronze">
                  <img src={url} alt="" className="w-full h-full object-contain" />
                </div>
                <div className="text-[11px] text-cmp-ink-2 truncate mt-1">
                  {a.originalFilename || a.sha256.slice(0, 10)}
                </div>
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
